import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Hospital,
  Stethoscope,
  FlaskConical,
  FileText,
  Ambulance,
  HeartHandshake,
  ShieldCheck,
  ArrowRight,
  Star,
  Globe,
  Heart,
  CalendarDays,
  Bell,
  ChevronRight,
  Users,
  Building2,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/authStore";
import logoWide from "@/assets/trata-health-logo-wide.png";

const PLAY_URL =
  "https://play.google.com/store/apps/details?id=app.tratahealth.patient&pcampaignid=web_share";

// Blue theme (scoped to this landing page only)
const BLUE = "#0D52CC"; // primary royal blue (buttons) — from reference
const BLUE_DARK = "#0A43A8"; // hover
const BLUE_ACCENT = "#0D52CC"; // bright accent / highlighted text
const NAVY = "#0F254A"; // headings
const BLUE_TINT = "#E8F1FF"; // soft chip / accent background

const FEATURES = [
  { icon: Ambulance, color: "#DC2626", title: "Emergency SOS", desc: "Get help instantly" },
  { icon: Stethoscope, color: "#2563EB", title: "Doctors", desc: "Consult online or in-person" },
  { icon: Hospital, color: "#059669", title: "Hospitals", desc: "Find & compare nearby hospitals" },
  { icon: FlaskConical, color: "#EC4899", title: "Diagnostics", desc: "Book tests at home or centres" },
  { icon: HeartHandshake, color: "#16A34A", title: "Home Care", desc: "Nursing, physiotherapy & more" },
  { icon: FileText, color: "#0369A1", title: "Health Records", desc: "All your reports in one place" },
];

// Rows shown inside the phone mockup
const PHONE_ROWS = [
  { icon: Ambulance, title: "Emergency SOS", desc: "Get immediate help", highlight: true },
  { icon: Stethoscope, title: "Find a Doctor", desc: "Consult online or in-person" },
  { icon: Hospital, title: "Hospitals", desc: "Find nearby hospitals" },
  { icon: FlaskConical, title: "Diagnostics", desc: "Book tests at home or centre" },
  { icon: CalendarDays, title: "Appointments", desc: "Manage your visits" },
  { icon: FileText, title: "Health Records", desc: "Access your reports" },
];

const STATS = [
  { icon: Users, value: "1,000+", label: "Users & families" },
  { icon: Building2, value: "Partnered", label: "Hospitals across India" },
  { icon: ShieldCheck, value: "Trusted & Secure", label: "Privacy-first platform" },
  { icon: MapPin, value: "Expanding", label: "Access to better healthcare" },
];

function AppleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M16.365 1.43c0 1.14-.42 2.2-1.12 2.98-.76.86-2 1.52-3.02 1.44-.13-1.1.44-2.26 1.1-3 .74-.82 2.03-1.44 3.04-1.42zM20.5 17.1c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.38 3.5-4.1 3.52-1.53.01-1.92-1-4-1-2.07 0-2.5.98-4.03 1.01-1.72.03-3.03-1.69-4.02-3.25C-.1 16.85-.9 11.3 1.36 8.2c1.08-1.5 2.78-2.45 4.4-2.45 1.65 0 2.68 1.01 4.05 1.01 1.32 0 2.13-1.01 4.04-1.01 1.44 0 2.96.78 4.05 2.14-3.56 1.95-2.98 7.03.6 9.21z" />
    </svg>
  );
}

function GooglePlayIcon(props) {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" {...props}>
      <path fill="#32BBFF" d="M99.6 48.1c-6.4 3.4-10.6 10.1-10.6 19.1v377.6c0 9 4.2 15.7 10.6 19.1l211-208L99.6 48.1z" />
      <path fill="#2C9FD9" d="M310.6 256.1l-211 207.8c.1 0 .2.1.3.1 6.8 3.7 15.3 3.5 24.3-1.5l233.9-128.2-47.5-78.2z" />
      <path fill="#29CC5E" d="M123.9 49.6c-9-5-17.5-5.2-24.3-1.5-.1 0-.2.1-.3.1l211 207.9 47.5-78.3L123.9 49.6z" />
      <path fill="#FFD500" d="M423.4 227.9l-65.3-35.8-50.7 64 50.7 64 65.5-35.9c19.3-10.6 19.3-45.7-.2-56.3z" />
    </svg>
  );
}

function PhoneMockup() {
  return (
    <div className="relative mx-auto w-[280px] sm:w-[300px]">
      {/* Phone frame */}
      <div className="relative rounded-[2.75rem] bg-[#0F254A] p-2.5 shadow-2xl">
        <div className="relative overflow-hidden rounded-[2.25rem] bg-white">
          {/* Notch */}
          <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-[#0F254A]" />
          {/* Screen content */}
          <div className="px-4 pb-5 pt-9">
            {/* App bar */}
            <div className="flex items-center justify-between">
              <img src={logoWide} alt="Trata Health" className="h-9 w-auto object-contain" />
              <Bell className="h-5 w-5" style={{ color: BLUE_ACCENT }} />
            </div>
            {/* Greeting */}
            <div className="mt-4">
              <p className="text-xs text-slate-500">Good Morning</p>
              <p className="text-lg font-bold" style={{ color: NAVY }}>Ananya</p>
              <p className="text-xs text-slate-500">How can we help you today?</p>
            </div>
            {/* Rows */}
            <div className="mt-3 space-y-2">
              {PHONE_ROWS.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                  style={
                    r.highlight
                      ? { backgroundColor: "#DC2626" }
                      : { backgroundColor: "#F4F8FF" }
                  }
                >
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: r.highlight ? "rgba(255,255,255,0.22)" : BLUE_TINT }}
                  >
                    <r.icon className="h-4 w-4" style={{ color: r.highlight ? "#fff" : BLUE_ACCENT }} />
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span
                      className="text-[13px] font-semibold"
                      style={{ color: r.highlight ? "#fff" : NAVY }}
                    >
                      {r.title}
                    </span>
                    <span
                      className="text-[10px]"
                      style={{ color: r.highlight ? "rgba(255,255,255,0.85)" : "#64748B" }}
                    >
                      {r.desc}
                    </span>
                  </span>
                  <ChevronRight
                    className="ml-auto h-4 w-4"
                    style={{ color: r.highlight ? "rgba(255,255,255,0.9)" : "#94A3B8" }}
                  />
                </div>
              ))}
            </div>
          </div>
          {/* Bottom tab bar */}
          <div className="flex items-center justify-around border-t border-slate-100 py-2.5">
            {[Globe, Stethoscope, Hospital, FileText].map((Icon, i) => (
              <Icon key={i} className="h-5 w-5" style={{ color: i === 0 ? BLUE_ACCENT : "#94A3B8" }} />
            ))}
          </div>
        </div>
      </div>

      {/* Floating trust badge */}
      <div className="absolute -bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-lg">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full"
          style={{ backgroundColor: BLUE_TINT }}
        >
          <Heart className="h-4 w-4 fill-current" style={{ color: BLUE_ACCENT }} />
        </span>
        <span className="whitespace-nowrap text-xs font-semibold" style={{ color: NAVY }}>
          Trusted by families across India
        </span>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    document.title = "Trata Health — Open Web App or Download the Mobile App";
    if (isAuthenticated) navigate("/home", { replace: true });
  }, [isAuthenticated, navigate]);

  const openWebApp = () => navigate("/auth");

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <img src={logoWide} alt="Trata Health" className="h-10 sm:h-11 md:h-12 lg:h-14 w-auto object-contain" />
          <Button
            variant="ghost"
            className="rounded-full font-semibold"
            style={{ color: BLUE }}
            onClick={openWebApp}
            data-testid="landing-login"
          >
            Log in
          </Button>
        </div>
      </header>

      {/* Hero — one pager */}
      <section className="relative overflow-hidden">
        {/* Soft blue background accents */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#2E6FF2]/10 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-24 h-80 w-80 rounded-full bg-[#2E6FF2]/5 blur-3xl" />

        <div className="relative max-w-6xl w-full mx-auto px-5 pt-10 md:pt-14 pb-16 grid md:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left */}
          <div className="animate-fade-in-up">
            <span
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full"
              style={{ color: BLUE, backgroundColor: BLUE_TINT }}
            >
              <Star className="h-3.5 w-3.5 fill-current" /> Your health, always with you
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mt-5 tracking-tight"
              style={{ color: NAVY }}
            >
              Healthcare that stays with you,{" "}
              <span style={{ color: BLUE_ACCENT }}>when it matters most.</span>
            </h1>
            <p className="text-lg text-slate-500 mt-5 max-w-lg">
              Trata Health connects you, your family, doctors and hospitals across the entire care
              journey — from emergency to recovery.
            </p>

            {/* Primary action: open web app */}
            <div className="mt-8">
              <Button
                size="lg"
                className="rounded-full h-14 px-8 text-base w-full sm:w-auto !text-white"
                style={{ backgroundColor: BLUE }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_DARK)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
                onClick={openWebApp}
                data-testid="hero-open-webapp"
              >
                <Globe className="h-5 w-5" /> Open Web App <ArrowRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Store badges */}
            <p className="text-sm font-medium mt-7" style={{ color: NAVY }}>Or get the mobile app</p>
            <div className="flex flex-wrap items-stretch gap-3 mt-3">
              {/* Google Play — live */}
              <a
                href={PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="badge-google-play"
                className="flex items-center gap-3 bg-black text-white rounded-xl px-4 h-14 hover:bg-black/85 transition-colors"
              >
                <GooglePlayIcon className="h-7 w-7 shrink-0" />
                <span className="flex flex-col leading-none text-left">
                  <span className="text-[10px] tracking-wide opacity-80">GET IT ON</span>
                  <span className="text-lg font-semibold -mt-0.5">Google Play</span>
                </span>
              </a>

              {/* App Store — coming soon */}
              <div
                data-testid="badge-app-store"
                className="relative flex items-center gap-3 bg-black/90 text-white rounded-xl px-4 h-14 opacity-80 cursor-default select-none"
              >
                <AppleIcon className="h-7 w-7 shrink-0" />
                <span className="flex flex-col leading-none text-left">
                  <span className="text-[10px] tracking-wide opacity-80">COMING SOON ON</span>
                  <span className="text-lg font-semibold -mt-0.5">App Store</span>
                </span>
                <span
                  className="absolute -top-2 -right-2 text-[10px] font-bold text-white px-2 py-0.5 rounded-full shadow"
                  style={{ backgroundColor: BLUE }}
                >
                  Soon
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" style={{ color: BLUE_ACCENT }} /> iOS app is coming soon — meanwhile, use the Web App on any device.
            </p>
          </div>

          {/* Right — phone mockup */}
          <div className="pt-4 md:pt-0">
            <PhoneMockup />
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-[#F4F8FF] border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-5 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ backgroundColor: f.color + "1A" }}>
                  <f.icon className="h-6 w-6" style={{ color: f.color }} />
                </div>
                <p className="mt-2 text-sm font-bold" style={{ color: f.color }}>{f.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-snug">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust stats */}
      <section className="border-t border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: BLUE_TINT }}
                >
                  <s.icon className="h-5 w-5" style={{ color: BLUE_ACCENT }} />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-base font-bold" style={{ color: NAVY }}>{s.value}</span>
                  <span className="text-xs text-slate-500">{s.label}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-slate-100 bg-gradient-to-br from-[#EAF2FF] via-white to-[#EAF2FF]">
        <div className="max-w-6xl mx-auto px-5 py-14 md:py-16 flex flex-col items-center text-center">
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full"
            style={{ color: BLUE, backgroundColor: BLUE_TINT }}
          >
            <Heart className="h-3.5 w-3.5 fill-current" /> Take control of your health today
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mt-4" style={{ color: NAVY }}>
            Download the <span style={{ color: BLUE_ACCENT }}>Trata Health</span> App
          </h2>
          <p className="text-base text-slate-500 mt-3 max-w-xl">
            Safe. Simple. Connected care — always with you. Book doctors, find hospitals, manage
            records and reach emergency help in one place.
          </p>

          <Button
            size="lg"
            className="rounded-full h-14 px-8 text-base mt-7 !text-white"
            style={{ backgroundColor: BLUE }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_DARK)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
            onClick={openWebApp}
            data-testid="cta-open-webapp"
          >
            <Globe className="h-5 w-5" /> Open Web App <ArrowRight className="h-4 w-4" />
          </Button>

          <div className="flex flex-wrap items-stretch justify-center gap-3 mt-5">
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-badge-google-play"
              className="flex items-center gap-3 bg-black text-white rounded-xl px-4 h-14 hover:bg-black/85 transition-colors"
            >
              <GooglePlayIcon className="h-7 w-7 shrink-0" />
              <span className="flex flex-col leading-none text-left">
                <span className="text-[10px] tracking-wide opacity-80">GET IT ON</span>
                <span className="text-lg font-semibold -mt-0.5">Google Play</span>
              </span>
            </a>
            <div
              data-testid="cta-badge-app-store"
              className="relative flex items-center gap-3 bg-black/90 text-white rounded-xl px-4 h-14 opacity-80 cursor-default select-none"
            >
              <AppleIcon className="h-7 w-7 shrink-0" />
              <span className="flex flex-col leading-none text-left">
                <span className="text-[10px] tracking-wide opacity-80">COMING SOON ON</span>
                <span className="text-lg font-semibold -mt-0.5">App Store</span>
              </span>
              <span
                className="absolute -top-2 -right-2 text-[10px] font-bold text-white px-2 py-0.5 rounded-full shadow"
                style={{ backgroundColor: BLUE }}
              >
                Soon
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-5 bg-white">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <img src={logoWide} alt="Trata Health" className="h-8 md:h-10 w-auto object-contain" />
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} Trata Health • The Missing Link</p>
        </div>
      </footer>
    </div>
  );
}
